import React, { useState } from 'react';
import PageShell from '../../components/PageShell';
import { Eyebrow, SectionHeading, GradientButton, CTABanner, VideoCard } from '../../components/common/Primitives';
import VideoLightbox from '../../components/common/VideoLightbox';
import ModuleCard from '../../components/common/ModuleCard';
import LicenceFeeHighlight from '../../components/common/LicenceFeeHighlight';
import GyanMitraSection from '../../components/common/GyanMitraSection';
import { EXPLORE_VR as V } from '../../lib/mockExplore';
import { FEATURED_VIDEO_ID, youtubeThumb } from '../../lib/moduleVideos';
import { PlayCircle } from 'lucide-react';

const explorationGradient = (i) => ['from-purple to-royal','from-royal to-cyan','from-cyan to-mint','from-mint to-royal','from-cyan to-purple','from-royal to-purple','from-purple to-mint','from-mint to-cyan'][i % 8];

export default function ExploreVRExperiences() {
  const d = V;
  const [heroOpen, setHeroOpen] = useState(false);
  return (
    <PageShell>
      {/* HERO */}
      <section className="px-6 pt-12 pb-8" data-testid="explore-vr-hero">
        <div className="mx-auto max-w-[1240px]">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.15fr_1fr]">
            <div>
              <Eyebrow color="purple">{d.hero.eyebrow}</Eyebrow>
              <h1 className="mt-3 text-[40px] md:text-[54px] font-extrabold leading-[1.05] tracking-tight text-navy">{d.hero.title}</h1>
              <p className="mt-5 max-w-xl text-[15.5px] leading-relaxed text-ink-500">{d.hero.desc}</p>
              <div className="mt-6"><LicenceFeeHighlight /></div>
              <div className="mt-6 flex flex-wrap gap-3">
                <GradientButton to={d.hero.ctaPrimary.to} data-testid="explore-vr-cta-primary">{d.hero.ctaPrimary.label}</GradientButton>
                <GradientButton to={d.hero.ctaSecondary.to} variant="outline" data-testid="explore-vr-cta-secondary">{d.hero.ctaSecondary.label}</GradientButton>
              </div>
            </div>
            <div className="rounded-3xl border border-canvas-200 bg-gradient-to-br from-purple-50 via-white to-cyan-50 p-6 md:p-8 shadow-soft">
              <button
                type="button"
                aria-label="Play immersive preview video"
                onClick={() => setHeroOpen(true)}
                data-testid="explore-vr-hero-video-play"
                className="group relative block w-full aspect-video rounded-2xl overflow-hidden focus:outline-none focus:ring-4 focus:ring-purple/30"
              >
                <img src={youtubeThumb(FEATURED_VIDEO_ID, 'maxresdefault')} alt="Immersive preview" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-black/35 group-hover:bg-black/25 transition-colors" />
                <span className="absolute inset-0 grid place-items-center"><PlayCircle className="h-14 w-14 text-white" /></span>
              </button>
              <div className="mt-4 text-[11.5px] font-bold tracking-widest text-purple">{d.hero.videoTag}</div>
              <p className="mt-1 text-[13px] text-ink-500 leading-relaxed">{d.hero.videoDesc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CURRICULUM */}
      <section className="px-6 py-12" data-testid="explore-vr-curriculum">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.curriculum.eyebrow} title={d.curriculum.title} desc={d.curriculum.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {d.curriculum.items.map((m, i) => (<ModuleCard key={i} {...m} />))}
          </div>
        </div>
      </section>

      {/* EXPLORATION / GYAN VATIKA */}
      <section className="px-6 py-12 bg-white" data-testid="explore-vr-exploration">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.exploration.eyebrow} title={d.exploration.title} desc={d.exploration.desc} />
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {d.exploration.items.map((m, i) => (
              <VideoCard key={i} title={m.title} chapter={m.subject} status="Exploration" gradient={explorationGradient(i)} />
            ))}
          </div>
        </div>
      </section>

      {/* PIPELINE */}
      <section className="px-6 py-12" data-testid="explore-vr-pipeline">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.pipeline.eyebrow} title={d.pipeline.title} desc={d.pipeline.desc} />

          <div className="mt-8">
            <div className="text-[11.5px] font-bold tracking-[0.14em] text-royal">IN DEVELOPMENT</div>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {d.pipeline.inDev.map((m, i) => (
                <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-4 shadow-soft">
                  <div className="inline-flex items-center gap-1 rounded-full bg-royal-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-royal">In development</div>
                  <div className="mt-3 text-[14px] font-bold text-navy">{m.title}</div>
                  <div className="mt-1 text-[11.5px] font-bold tracking-[0.12em] text-purple">{m.subject.toUpperCase()}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10">
            <div className="text-[11.5px] font-bold tracking-[0.14em] text-purple">UPCOMING</div>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {d.pipeline.upcoming.map((m, i) => (
                <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-4 shadow-soft">
                  <div className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">Upcoming</div>
                  <div className="mt-3 text-[14px] font-bold text-navy">{m.title}</div>
                  <div className="mt-1 text-[11.5px] font-bold tracking-[0.12em] text-purple">{m.subject.toUpperCase()}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* LANGUAGES AND FORMAT */}
      <section className="px-6 py-12 bg-white" data-testid="explore-vr-languages">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.languages.eyebrow} title={d.languages.title} desc={d.languages.desc} />
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {d.languages.items.map((l, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft text-center">
                <div className={`mx-auto grid h-14 w-14 place-items-center rounded-full bg-${l.color} text-white text-[16px] font-extrabold`}>{l.k}</div>
                <div className="mt-3 text-[14px] font-bold text-navy">{l.label}</div>
                <p className="mt-1 text-[12px] leading-relaxed text-ink-500">{l.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <GyanMitraSection />

      <section className="px-6 pb-24" data-testid="explore-vr-cta">
        <div className="mx-auto max-w-[1240px]">
          <CTABanner title={d.cta.title} desc={d.cta.desc} primary={d.cta.primary} secondary={d.cta.secondary} />
        </div>
      </section>

      {heroOpen && (
        <VideoLightbox videoId={FEATURED_VIDEO_ID} title="Immersive preview" onClose={() => setHeroOpen(false)} />
      )}
    </PageShell>
  );
}
